import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);
export const useCurrency = () => useContext(CurrencyContext);

// Currencies offered in the storefront switcher (converted from the GBP base price).
export const CURRENCIES = {
  GBP: "British Pound",
  USD: "US Dollar",
  EUR: "Euro",
  CAD: "Canadian Dollar",
  AUD: "Australian Dollar",
  NGN: "Nigerian Naira",
  ZAR: "South African Rand",
  AED: "UAE Dirham",
  GHS: "Ghanaian Cedi",
  KES: "Kenyan Shilling",
};

const REGION_CCY = {
  GB: "GBP", US: "USD", CA: "CAD", AU: "AUD", NG: "NGN", ZA: "ZAR", AE: "AED", GH: "GHS", KE: "KES",
  IE: "EUR", DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", PT: "EUR", BE: "EUR", AT: "EUR", GR: "EUR", FI: "EUR",
};

const detect = () => {
  try {
    const saved = localStorage.getItem("nba_currency");
    if (saved && CURRENCIES[saved]) return saved;
    const l = new Intl.Locale(navigator.language || "en-GB");
    const region = (l.maximize && l.maximize().region) || l.region;
    return REGION_CCY[region] || "GBP";
  } catch { return "GBP"; }
};

// Snap converted prices to tidy, deliberate-looking numbers per magnitude
// (e.g. 447,606 -> 450,000; 331.35 -> 330) so they never look machine-converted.
const niceRound = (v) => {
  if (v <= 0) return 0;
  let step;
  if (v < 10) step = 1;
  else if (v < 100) step = 5;
  else if (v < 1000) step = 10;
  else if (v < 10000) step = 100;
  else if (v < 100000) step = 1000;
  else if (v < 1000000) step = 10000;
  else step = 100000;
  return Math.round(v / step) * step;
};

export const CurrencyProvider = ({ children }) => {
  const [rates, setRates] = useState({ GBP: 1 });
  const [currency, setCurrencyState] = useState(detect);

  useEffect(() => {
    api.get("/fx/rates").then((r) => setRates(r.data.rates || { GBP: 1 })).catch(() => {});
  }, []);

  const setCurrency = useCallback((c) => {
    if (!CURRENCIES[c]) return;
    setCurrencyState(c);
    try { localStorage.setItem("nba_currency", c); } catch { /* ignore */ }
  }, []);

  const format = useCallback((gbp) => {
    if (gbp === null || gbp === undefined || gbp === "") return "Price on request";
    const rate = rates[currency] ?? 1;
    let val = Number(gbp) * rate;
    let digits = 2;
    if (currency !== "GBP") { val = niceRound(val); digits = 0; }
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
      }).format(val);
    } catch { return `£${Number(gbp).toFixed(2)}`; }
  }, [rates, currency]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, format, currencies: CURRENCIES, isConverted: currency !== "GBP" }}>
      {children}
    </CurrencyContext.Provider>
  );
};
